import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';

import { SignupSessionError } from '@/auth';
import { SignupCredentialsForm } from '@/auth/components/SMBSignupModal/SignupCredentialsForm';
import type { SMBSignupFormData } from '@/auth/components/SMBSignupModal/types';
import { appendSignupParams } from '@/auth/utils/signupParams';
import { ERROR_CODES } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import type { AccountData } from '../types';
import { accountStepFieldErrors } from '../utils/accountStepErrors';

function errorHasCode(err: unknown, code: string): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code?: unknown }).code === code
  );
}

// Step A — account creation. After ENG-5201's 2026-05-08 phoneless pivot,
// this step delegates the form body to the shared SignupCredentialsForm
// (extracted from SMBSignupForm) so the visual is pixel-identical to the
// legacy "Get Free Access" gate. Outer chrome (Box padding, header
// Typography, subtitle) mirrors SMBSignupModal's `<Box sx={{ p: 3 }}>`
// content area.
//
// On success: handleSubmit calls onSignup (createUser w/ signup_subtype:
// "self_service" + authSession.authenticate), then dispatches
// flow.accountCreated to advance the reducer to the email-otp step.
// Email-already-exists errors are mapped to the email field via React
// Final Form ValidationErrors so they surface inline (matches SMBSignupForm
// behavior; resolves the original "error fires after OTP" bug).

interface AccountStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  onSignup: (data: AccountData) => Promise<void>;
}

export function AccountStep({ flow, onSignup }: AccountStepProps): JSX.Element {
  const { search } = useLocation();
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [mfaRequiredEmail, setMfaRequiredEmail] = useState<string | null>(null);
  const [signInRequiredMessage, setSignInRequiredMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: SMBSignupFormData) => {
      const data: AccountData = {
        firstName: values.first_name.trim(),
        lastName: values.last_name.trim(),
        email: values.email.trim(),
        password: values.password,
        // companyName collected on the team-create step, not here.
        companyName: '',
        acceptTerms: !!values.accept_privacy_and_terms_of_use,
      };
      try {
        setBusy(true);
        setSubmitError(null);
        setMfaRequiredEmail(null);
        setSignInRequiredMessage(null);
        flow.clearError();
        await onSignup(data);
        flow.accountCreated(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Signup failed';
        // Map API server-side errors to React Final Form field-level errors
        // so they surface inline next to the offending input (matches the
        // SMBSignupForm pattern in useSMBSignup.ts).
        const fieldErrors = accountStepFieldErrors(err);
        if (fieldErrors) return fieldErrors;
        // The signup mutation succeeded server-side but createToken bounced
        // because the account is enrolled in (or needs to enroll) MFA. The
        // user can't retry this form — their account exists. Route them to
        // the login flow which knows how to drive MFA setup.
        if (errorHasCode(err, ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED)) {
          setMfaRequiredEmail(data.email);
          return undefined;
        }
        // Same dead end, any other token failure (ENG-5760): the account
        // exists, so resubmitting can only hit "already exists".
        if (err instanceof SignupSessionError) {
          setSignInRequiredMessage(err.message);
          return undefined;
        }
        setSubmitError(message);
      } finally {
        setBusy(false);
      }
      return undefined;
    },
    [flow, onSignup]
  );

  if (mfaRequiredEmail || signInRequiredMessage) {
    const copy =
      signInRequiredMessage ??
      getDefaultErrorMessage(ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED) ??
      'Your account needs multi-factor authentication set up before you can continue.';
    // Carry campaign params through the login round-trip, or an MFA-enrolled
    // user silently loses their trial on the way back (ENG-5527).
    const loginHref = `/login?next=${encodeURIComponent(
      appendSignupParams('/signup/customer', search)
    )}`;
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2.75 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5, fontSize: 24 }}>
            Finish setup
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
            {copy}
          </Typography>
          <Link
            component={RouterLink}
            to={loginHref}
            sx={{ fontWeight: 600, color: 'var(--brandPrimary)' }}
          >
            Sign in to continue
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2.75, pl: '2px', pr: '7px' }}>
        <Typography
          id="self-service-smb-account-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 700, mb: 1.5, fontSize: 24 }}
        >
          {STEP_COPY.account.formTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {STEP_COPY.account.formSubtitle}
        </Typography>
      </Box>
      <SignupCredentialsForm
        onSubmit={handleSubmit}
        loading={busy}
        error={submitError}
        submitLabel={STEP_COPY.account.submit}
        submittingLabel="Creating account…"
      />
      {/* Mirrors SMBSignupForm's footer. Uses the React Router Link so the
          modal context can unmount cleanly — the previous hard navigation
          existed to force Apollo + PermissionService re-init, both of
          which are now handled by authSession.authenticate(). */}
      <Box sx={{ textAlign: 'center', mt: 1, fontSize: 14 }}>
        Already have an account?{' '}
        <Box
          component={RouterLink}
          to="/login?next=/dashboard"
          sx={{
            color: 'var(--brandPrimary)',
            textDecoration: 'none',
            '&:hover': { opacity: 0.8 },
          }}
        >
          Sign in
        </Box>
      </Box>
    </Box>
  );
}
